import { useState, useEffect } from 'react';
import { API_BASE_URL, fetchWithCSRF } from '../config';

/**
 * Custom hook for fetching and managing report data
 */
export function useReportData(uuid) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [schoolPreferences, setSchoolPreferences] = useState({});
  const [studentProfile, setStudentProfile] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      if (!uuid) return;

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/universities/reports/${uuid}/`);

        if (!response.ok) {
          throw new Error('Report not found');
        }

        const data = await response.json();

        // Transform API data to match component structure
        const transformedFormData = {
          gpa: data.gpa,
          satScore: data.sat_score,
          actScore: data.act_score,
          psatScore: data.psat_score,
          grade: data.grade,
          state: data.state,
          schools: data.schools_interested,
          majors: data.majors || [],
          knowsMajor: data.knows_major,
          answerer: data.answerer,
          contactEmail: data.contact_email,
          firstName: data.first_name,
          lastName: data.last_name
        };

        setFormData(transformedFormData);
        setRecommendations(data.recommendations);
        setSchoolPreferences(data.school_preferences || {});
        setStudentProfile(data.recommendations?.student_profile || null);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchReport();
  }, [uuid]);

  const updatePreference = async (schoolId, preference) => {
    try {
      // Optimistically update UI
      setSchoolPreferences(prev => ({
        ...prev,
        [schoolId]: preference
      }));

      // Update backend
      const response = await fetchWithCSRF(
        `${API_BASE_URL}/api/universities/reports/${uuid}/preferences/${schoolId}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ preference })
        }
      );

      if (!response.ok) {
        // Revert on error
        setSchoolPreferences(prev => {
          const newPrefs = { ...prev };
          delete newPrefs[schoolId];
          return newPrefs;
        });
        console.error('Failed to update preference');
      }
    } catch (error) {
      console.error('Error updating preference:', error);
    }
  };

  const saveEmail = async (email) => {
    const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/reports/${uuid}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ contact_email: email })
    });

    if (response.ok) {
      setFormData(prev => ({ ...prev, contactEmail: email }));
      return true;
    }
    return false;
  };

  return {
    loading,
    error,
    formData,
    recommendations,
    setRecommendations,
    schoolPreferences,
    studentProfile,
    setStudentProfile,
    updatePreference,
    saveEmail
  };
}